<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Fill casks"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row dense>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh"/>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" lg="4">
              <tank-select v-model="transaction.tankId" :tanks="tanks" label="From tank" outlined :hint="transactionId===undefined"
                           @change="changeTank"/>
            </v-col>
            <v-col cols="6" sm="2">
              <decimal-number-input label="Cask fill strength" v-model="transaction.fillStrength"  prefix="" suffix="% alc./vol." :precision="2" :max="99.99"
                                    :disabled="!edit"/>
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field label="Lot code" outlined dense v-model="transaction.lot" :disabled="!edit" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
            </v-col>

            <v-col cols="6" sm="2">
              <decimal-number-input label="Age of spirit to add" v-model="transaction.age" prefix="" :suffix="period" :precision="period==='days'?0:1"/>
            </v-col>
            <v-col cols="3" sm="1">
              <v-switch v-model="period" label="Days" hide-details class="mt-1 ml-1" false-value="years" true-value="days"
                        @change="transaction.age = period === 'days' ? transaction.age * 365 : transaction.age / 365"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <outlined-block v-model="transaction.casks" :rules="[inputsNeeded]" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="transaction.casks" no-data-text="At least one cask is required" item-key="key"
                              :headers="[{text:'',value:'add',width:'1%',align:'center',class:'pa-0',cellClass:'pa-0',divider:true},
                                          {text:'Cask',value:'cask',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                          {text:'Empty weight',value:'empty',width:'12%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                          {text:'Filled Weight',value:'full',width:'12%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                          {text:'Volume to add',value:'laa',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                          {text:'Info',value:'info',width:'30%',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                          {text:'Location',value:'location',width:'16%',divider:true,class:'px-2',cellClass:'py-2 px-2'}]"  class="no-sort"
                              :item-class="item => item.key=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:item.add="{item,index}">
                    <v-btn :disabled="!edit" icon @click.stop="deleteInput(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
                  </template>

                  <template v-slot:item.cask="{item}">
                    <template v-if="selectedItem === item.key">
                      <v-select v-model="item.source" :items="caskOptions(item)" outlined dense
                                @change="item.source === 'add' ? getCaskInfo(item) : ''"/>
                      <v-autocomplete label="Empty cask #" v-if="item.source==='empty'" v-model="item.caskId" :items="eligibleEmptyCasks(item)" item-text="number" item-value="id" outlined dense/>
                      <v-text-field v-else v-model="item.number" dense outlined label="Cask #"
                                    :rules="[fieldRules.required,fieldRules.maxLength(10)]"
                                    @change="getCaskInfo(item)"/>
                      <div v-if="item.source==='add' && item.numberError" class="error--text mt-n5 mb-2">Not found</div>
                      <v-text-field v-if="item.source==='empty'" v-model="item.number" dense outlined label="Assign new cask #"
                                    :rules="[fieldRules.required,fieldRules.maxLength(10)]"/>
                    </template>
                    <template v-else>
                      <template v-if="item.source==='empty'">
                        <div>Empty cask #{{eligibleEmptyCasks(item).find(c => c.id === item.caskId).number}}</div>
                        <div>(Assign new #{{item.number}})</div>
                      </template>
                      <div v-else-if="item.source==='new'">New cask #{{item.number}}</div>
                      <template v-else-if="item.source==='add'">
                        <div>Add to cask #{{item.number}}</div>
                        <div v-if="item.numberError" class="error--text">Not found</div>
                      </template>
                    </template>
                  </template>
                  <template v-slot:item.empty="{item}">
                    <template>
                      <decimal-number-input v-if="selectedItem === item.key" v-model="item.tare" label="Empty weight" prefix="" suffix="kg" :precision="3" @change="calculateLAA(item)"/>
                      <div v-else :class="{'error--text':item.tare<0}">{{item.tare | number(3)}} kg</div>
                    </template>
                  </template>
                  <template v-slot:item.full="{item}">
                    <decimal-number-input v-if="selectedItem === item.key" v-model="item.fillWeight" label="Filled weight" prefix="" suffix="kg" :precision="3" @change="calculateLAA(item)"/>
                    <div v-else :class="{'error--text':item.fillWeight<0}">{{item.fillWeight | number(3)}} kg</div>
                  </template>
                  <template v-slot:item.laa="{item}">
                    <alcohol-input v-if="selectedItem === item.key" dont-show-strength-and-kg
                                    spirit class="text-body-2" label="Volume"
                                    :tare="item.tare"
                                    v-model="item.volume" :litres.sync="item.litres"/>
                    <template v-else>
                      <div :class="{'error--text':item.volume<0}">{{item.volume | number(3)}} LAA</div>
                      <div v-if="trackLitres" :class="{'error--text':item.litres<0}">{{item.litres | number(3)}} litres</div>
                    </template>
                  </template>
                  <template v-slot:item.info="{item}">
                    <template>
                      <v-text-field v-if="selectedItem === item.key" v-model="item.info" outlined label="Info" dense class="text-caption" :rules="[fieldRules.maxLength(200)]"/>
                      <div v-else class="text-caption">{{item.info}}</div>
                    </template>
                  </template>
                  <template v-slot:item.location="{item}">
                    <template>
                      <v-text-field v-if="selectedItem === item.key" v-model="item.location" outlined label="Location" dense class="text-caption" :rules="[fieldRules.maxLength(20)]"/>
                      <div v-else class="text-caption">{{item.location}}</div>
                    </template>
                  </template>
                  <template v-slot:body.append="{headers}">
                    <tr class="no-hover" @click="selectedItem = null">
                      <td class="text-center">
                        <v-btn :disabled="!edit" fab color="primary" x-small @click.stop="addInput"><v-icon small>mdi-plus</v-icon></v-btn>
                      </td>
                      <td :colspan="headers.length-1"/>
                    </tr>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row dense v-if="connectedToAccounting && values">
            <v-col cols="12" sm="6" lg="4">
              <account-picker v-model="transaction.inventoryAccountRef" label="Cask inventory asset account"
                              :accounts="accounts" :addTypes="['OtherCurrentAsset']"
                              hint="The inventory account used to track the value of cask contents"/>
            </v-col>
          </v-row>

          <v-row dense >
            <v-col cols="12">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense counter="255" :rules="[fieldRules.maxLength(255)]"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('production.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
    <confirm-leave v-model="next"/>
  </div>
</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'

export default {
  name: 'EquipmentTanksFillCask',
  mixins: [confirmLeave],
  components: { TankSelect },
	props: {
    tankId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      valid:true,
      saving:false,
      accounts: [],
      inputsNeeded: v => v.length > 0 || 'Casks needed',
      transaction: {tankId:'',date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    fillStrength:0,comments:'',casks:[],lot:'',age:0,inventoryAccountRef:''},
      nextNumber: '',
      tanks: [],
      emptyCasks: [],
      dirty: false,
      selectedItem: null,
      period: 'days'
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    },
    tank() {
      return this.tanks.find(tank => tank.id === this.transaction.tankId);
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    trackLitres() {
      return this.company.settings.trackLitres;
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    const results = await this.$apiCall(`query EquipmentTanksFillCask($currency:String!, $accounting:Boolean!,$id:ID!,$edit:Boolean!) {
                                            tankFillCask(id:$id) @include(if:$edit) {
                                              id,tankId,date,comments,fillStrength,comments,lot,age,inventoryAccountRef
                                              casks {caskId,newCask,number,tare,fillWeight,volume,litres,info,location}
                                            }
                                            accounts(currency:$currency) @include(if:$accounting) {
                                              ref,name,type
                                            }
                                            nextCaskNumber @skip(if:$edit)
                                            companyAccounts @skip(if:$edit) {caskInventory}
                                          }`,
                                        {accounting:this.connectedToAccounting,
                                         currency:this.company.settings.homeCurrency,
                                         id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.transactionId) {
      this.transaction = results.data.tankFillCask;
      let key = Date.now();
      this.transaction.casks.forEach(cask => {cask.key = key++; cask.numberError = false; cask.source = cask.newCask?'new':'add'});
      this.period = 'days';
    }
    else
      this.transaction.inventoryAccountRef = results.data.companyAccounts.caskInventory;
    this.nextNumber = results.data.nextCaskNumber;
    if (this.connectedToAccounting)
      this.accounts = results.data.accounts.filter(account => account.type === 'OtherCurrentAsset');
    await this.refresh();
    if (!this.transactionId) {
      this.transaction.tankId = this.tankId ?? this.tanks[0].id;
      this.changeTank();
      this.addInput();
      this.edit = true;
    }
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    eligibleEmptyCasks(cask) {
      return this.emptyCasks.filter(c => !this.transaction.casks.some(tc => tc.key !== cask?.key && tc.source === 'empty' && tc.caskId === c.id));
    },
    caskOptions(cask) {
      const options = [{text:'Create new',value:'new'},{text:'Add to',value:'add'}];
      const casks = this.eligibleEmptyCasks(cask);
      if (casks.length) {
        options.splice(0, 0, {text:'From empty',value:'empty'});
        if (cask.source === 'empty' &&  (!cask.caskId || !casks.find(c => c.id === cask.caskId)))
          cask.caskId = casks[0].id;
      }
      return options;
    },
    async getCaskInfo(cask) {
      if (cask.source === 'add') {
        const results = await this.$apiCall(`query EquipmentTanksFillCask($filters: [FilterInput]) {
                                               casks2(first:1, filters:$filters) {
                                                  edges {
                                                    node {
                                                      caskId:id,info,location,tare,number
                                                    }
                                                  }
                                               }
                                             }`, {filters:[{field:'number', comparator:'eq', value:cask.number}]});
        if (!results.error) {
          const caskInfo = results.data.casks2.edges[0]?.node;
          Object.assign(cask, caskInfo, {numberError:!caskInfo});
          if (cask.numberError)
            cask.caskId = '';
        }
      }
    },
    async calculateLAA(cask) {
      if (!cask.volume && !this.transaction.strength && cask.fillWeight > cask.tare) {
        const results = await this.$apiCall(`query EquipmentTanksFillCask($temp: Float!, $b: Float!) {
                                                alcoholometricLookupByStrength(temp:$temp, b:$b) {a,b}
                                              }`,
                                            {temp:20,b:this.transaction.fillStrength});
        if (!results.error) {
          const lookup = results.data.alcoholometricLookupByStrength;
          cask.volume = (cask.fillWeight - cask.tare) * lookup.a * lookup.b / 100;
        }
      }
    },
    changeTank() {
      this.transaction.lot = this.tank.contents.lot;
      this.transaction.age = this.tank.contents.spiritAge ?? 0;
      if (this.tank.contents.strength)
        this.transaction.fillStrength = this.tank.contents.strength;
      else
        this.transaction.fillStrength = this.tank.contents.litres ? this.tank.contents.volume / this.tank.contents.litres * 100
                                        : this.transaction.fillStrength;
    },
    getNextCaskNumber() {
      let number = this.nextNumber;
      if (this.transaction.casks.length)
        number = this.transaction.casks.reduce((max, cask) => Math.max(max, parseInt(cask.number||this.nextNumber)), 0) + 1 + '';
      return isNaN(number) ? '' : number;
    },
    addInput() {
      const number = this.getNextCaskNumber();
      const cask = {newCask:true,number,tare:0,fillWeight:0,volume:0,litres:0,info:'',location:'',
                    source:this.eligibleEmptyCasks().length ? 'empty' : 'new',
                    numberError: false, key:Date.now()};
      this.transaction.casks.push(cask);
      this.selectedItem = cask.key
    },
    deleteInput(index) {
      this.transaction.casks.splice(index, 1);
    },
    async refresh() {
      this.emptyCasks = [];
      this.tanks = [];
      await Promise.all([
        this.$lazyListLoad(this.tanks, 'tanks2', `id,name,tare,contents{volume,litres,alcoholName,alcoholType,lot,spiritAge,strength}`, 100, this.transactionId?[]:[{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.transaction.date}]),
        this.$lazyListLoad(this.emptyCasks, 'casks2', `id,number`, 100, [{field:'contents.volume', comparator:'eq', value:'0'},{field:'retired', comparator:'eq', value:'false'}], [{name:'asof', value:this.transaction.date}])
      ]);
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (!this.valid || this.transaction.casks.some(cask => cask.number.trim() === '' ||
                                                             cask.newCask && cask.tare < 0 ||
                                                             cask.fillWeight < 0 ||
                                                             cask.volume < 0 ||
                                                             cask.source === 'add' && cask.numberError ||
                                                             this.trackLitres && cask.litres < 0)) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      this.saving = true;
      const input = {...this.transaction};
      input.casks = this.transaction.casks.map(cask => {
        const {key,numberError,source, ...caskData} = cask;
        caskData.newCask = cask.source === 'new';
        return caskData;
      });
      input.age = this.period === 'days' ? input.age : input.age * 365;
      const results = await this.$apiCall(`mutation EquipmentTanksFillCask($input: TankFillCaskInput!) {
                                              tankFillCaskCreate(input:$input) {id}
                                            }`,
                                          {input});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} fill casks transaction for ${this.tank.name}`});
      }
		}
	}
}
</script>